import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ChevronDown, X, Loader2 } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';

export const MultiSelect = ({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Seleccionar...',
  icon: Icon,
  label,
  testId,
  disabled = false,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);

  const allSelected = selected.length === options.length;
  const noneSelected = selected.length === 0;

  const toggleItem = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const selectAll = () => onChange(options.map((o) => o.value));
  const selectNone = () => onChange([]);

  const displayText = noneSelected
    ? placeholder
    : allSelected
      ? 'Todas'
      : `${selected.length} seleccionada${selected.length > 1 ? 's' : ''}`;

  return (
    <div className="space-y-1">
      {label && (
        <label className="text-xs text-[#888888] flex items-center gap-1">
          {Icon && <Icon size={12} />}
          {label}
        </label>
      )}
      <Popover open={open} onOpenChange={(v) => !disabled && !loading && setOpen(v)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            data-testid={testId}
            className={`w-full h-9 justify-between text-sm font-normal border-input px-3 ${disabled ? 'bg-[#F5F5F5] cursor-not-allowed opacity-60' : 'hover:bg-accent'}`}
          >
            <span className="truncate text-left flex-1">
              {loading ? 'Cargando...' : displayText}
            </span>
            {loading ? (
              <Loader2 size={14} className="ml-1 shrink-0 animate-spin text-[#9d8272]" />
            ) : !noneSelected && !allSelected ? (
              <X
                size={14}
                className="ml-1 shrink-0 opacity-50 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  selectNone();
                }}
              />
            ) : (
              <ChevronDown size={14} className="ml-1 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#E8E8E7]">
            <button
              onClick={selectAll}
              className="text-xs text-[#9d8272] hover:underline font-medium"
              data-testid={`${testId}-select-all`}
            >
              Todas
            </button>
            <button
              onClick={selectNone}
              className="text-xs text-[#888888] hover:underline"
              data-testid={`${testId}-select-none`}
            >
              Ninguna
            </button>
          </div>
          <ScrollArea className="max-h-[200px]">
            <div className="p-1">
              {options.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-[#F5F5F5] cursor-pointer"
                  onClick={() => toggleItem(option.value)}
                  data-testid={`${testId}-option-${option.value}`}
                >
                  <Checkbox
                    checked={selected.includes(option.value)}
                    className="h-4 w-4 border-[#ccc]"
                    onCheckedChange={() => toggleItem(option.value)}
                  />
                  <span className="text-sm text-[#2D2D2D] truncate">{option.label}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
};
